import { FC } from 'react';
import type { Assessment } from '@libs/types';
import { Box, Button, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { Badge } from '@components/Badge';
import { useTrans } from '@libs/i18n';

import { ScoreBar } from './ScoreBar';

const Stat: FC<{
  label: string;
  value: string;
  hint?: string;
  emphasis?: string;
  children?: React.ReactNode;
}> = observer(({ label, value, hint, emphasis, children }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        p: 1.75,
        minWidth: 0,
        borderInlineStart: `1px solid ${theme.palette.grey[200]}`,
        '&:first-of-type': { borderInlineStart: 'none' },
      }}
    >
      <Typography
        variant="textXS_semiBold"
        sx={{
          display: 'block',
          textTransform: 'uppercase',
          letterSpacing: '0.03em',
          color: 'text.secondary',
        }}
      >
        {label}
      </Typography>
      {children ?? (
        <Typography
          variant="displayXS_bold"
          sx={{ display: 'block', lineHeight: 1.2, mt: 0.25, color: emphasis ?? 'text.primary' }}
        >
          {value}
        </Typography>
      )}
      {hint && (
        <Typography variant="textXS_regular" sx={{ display: 'block', color: 'text.tertiary' }}>
          {hint}
        </Typography>
      )}
    </Box>
  );
});

/**
 * What the scope is being chosen against.
 *
 * The page previously carried the assessment's name, score and date in a thin
 * strip above the picker, which named the assessment without saying anything
 * about it. The counts below the selection only mean something next to the
 * assessment's own numbers: "3 missing, 6 weak" is a different decision at 44%
 * with 29 gaps than at 91% with 2.
 *
 * Deliberately not the design's full stat strip. That mock also shows a severity
 * split under the gap count ("5 high · 8 medium · 4 low"), a score delta against
 * the previous version, and who completed the assessment — none of which this
 * page can source. The list row carries a total gap count and nothing finer, and
 * `createdBy` is a Keycloak id rather than a name. Rendering those from anything
 * we have would mean inventing them, so they are left out.
 */
export const ScopeAssessmentSummary: FC<{
  assessment: Assessment;
  language: string;
  /** Missing documents that would be written from scratch. */
  generateCount: number;
  /** Uploads that scored below full maturity. */
  remediateCount: number;
  onViewAssessment: () => void;
}> = observer(
  ({ assessment, language, generateCount, remediateCount, onViewAssessment }) => {
    const theme = useTheme();
    const { t } = useTrans();

    const meta = [
      assessment.language?.label,
      assessment.completedAt
        ? t('app.remediation.scope.summary.completedOn', {
            date: new Date(assessment.completedAt).toLocaleDateString(language),
          })
        : null,
    ].filter(Boolean) as string[];

    return (
      <Box sx={{ border: `1px solid ${theme.palette.grey[300]}`, borderRadius: 3, overflow: 'hidden' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
            p: 2,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="textMD_semiBold" sx={{ overflowWrap: 'anywhere' }}>
                {assessment.title}
              </Typography>
              <Badge variant="neutral" label={`v${assessment.version}`} />
            </Box>
            {meta.length > 0 && (
              <Typography
                variant="textXS_regular"
                sx={{ display: 'block', color: 'text.tertiary', mt: 0.5 }}
              >
                {meta.join(' · ')}
              </Typography>
            )}
          </Box>
          <Button variant="tertiary" onClick={onViewAssessment}>
            {t('app.remediation.scope.summary.viewAssessment')}
          </Button>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
            borderTop: `1px solid ${theme.palette.grey[200]}`,
          }}
        >
          <Stat label={t('app.remediation.scope.summary.score')} value="">
            <Box sx={{ mt: 0.75 }}>
              <ScoreBar score={assessment.score} />
            </Box>
          </Stat>
          <Stat
            label={t('app.remediation.scope.summary.gaps')}
            // Null means never graded, which is not zero — see `deriveGaps`.
            value={assessment.gaps == null ? '—' : String(assessment.gaps)}
            emphasis={assessment.gaps ? theme.palette.error.main : undefined}
          />
          <Stat
            label={t('app.remediation.scope.summary.missing')}
            value={String(generateCount)}
            hint={t('app.remediation.scope.summary.missingHint')}
          />
          <Stat
            label={t('app.remediation.scope.summary.weak')}
            value={String(remediateCount)}
            hint={t('app.remediation.scope.summary.weakHint')}
          />
        </Box>
      </Box>
    );
  },
);
